import { z } from 'zod';

import { MAX_CUSTOM_TIP_CENTS } from './tip-config-schemas.js';

// ── Payment schemas — JQ4-36 · US-PAGOS-002 · Visitor Payment ────────────────
//
// Shared contracts for the visitor payment API surfaces:
//   GET  /visitor/payment/config  → PaymentConfigResponse
//   POST /visitor/payment/intent  → CreatePaymentIntentResponse
//   GET  /visitor/payment/status  → PaymentStatusResponse
//
// Design decisions:
//   - All monetary values are integer cents (no floats, REQ-1.2).
//   - tipAmountCents is 0 when no tip selected; server-validated (JQ4-37).
//   - paymentAvailable reflects chargesEnabled && status=connected.
//   - publishableKey is intentionally absent — FE uses VITE_STRIPE_PLATFORM_PK
//     at build time; this endpoint returns stripeAccountId for { stripeAccount }.
//
// JQ4-37 additions:
//   - VisitorTipConfigSchema: tip config returned in PaymentConfigResponse.
//   - CreatePaymentIntentRequestSchema: accepts optional tipAmountCents.
//   - PaymentConfigResponse.tipConfig: null when tips disabled or no config row.

// ── ChargeBreakdown ──────────────────────────────────────────────────────────

export const ChargeBreakdownSchema = z.object({
  /** Base parking/hotel fare in cents — server-authoritative (REQ-10.1). */
  baseFareCents: z.number().int().nonnegative(),
  /** Tip amount in cents — always 0 in JQ4-36 (JQ4-37 deferred, REQ-E14). */
  tipAmountCents: z.number().int().nonnegative(),
  /** baseFareCents + tipAmountCents. Integer cents (REQ-1.2). */
  totalCents: z.number().int().nonnegative(),
});

export type ChargeBreakdown = z.infer<typeof ChargeBreakdownSchema>;

// ── VisitorTipConfig (JQ4-37) ─────────────────────────────────────────────────
// Returned inside PaymentConfigResponse when tipsEnabled=true for the location.
// null when tips are disabled or no config row exists.

export const VisitorTipConfigSchema = z.object({
  suggestedPercentages: z.array(z.number().int()),
  customAllowed: z.boolean(),
});

export type VisitorTipConfig = z.infer<typeof VisitorTipConfigSchema>;

// ── PaymentConfigResponse ─────────────────────────────────────────────────────

export const PaymentConfigResponseSchema = z.object({
  /**
   * Connected account id (acct_xxx).
   * FE uses: loadStripe(VITE_STRIPE_PLATFORM_PK, { stripeAccount: stripeAccountId }).
   */
  stripeAccountId: z.string().min(1),
  /** Amount breakdown shown before confirmation (S6 / CAP-1). */
  amount: ChargeBreakdownSchema,
  /** ISO 4217 3-letter currency code from location record (REQ-1.3). */
  currency: z.string().length(3),
  /**
   * true only when org has a connected account AND charges_enabled=true (REQ-8.3).
   * If false the FE must NOT render the payment form.
   */
  paymentAvailable: z.boolean(),
  /**
   * JQ4-37 — Tip config for the visitor. Non-null only when tipsEnabled=true.
   * null means no tip UI should be shown (tips disabled or no config row).
   */
  tipConfig: VisitorTipConfigSchema.nullable(),
  /**
   * flash-batch-sms-pay-clarity — non-null only when the ticket is linked to
   * an EventGuest whose billingMode is 'host' or 'free' (no charge applies
   * for this visitor). Lets the FE distinguish "covered by host / no charge"
   * from the existing generic "payment unavailable" (Stripe not connected).
   * Additive, backward compatible — null for all pre-existing flows.
   */
  noChargeReason: z.enum(['host_billed', 'free']).nullable(),
  /**
   * post-payment-delay-recharge S3b — server-computed amount (cents) owed for
   * a post-pickup delay recharge, 0 when none is owed (unpaid initial charge,
   * `readyForPickupAt` unset, or still within grace). The visitor FE shows a
   * re-checkout state (SEPARATE Stripe PaymentIntent) only when this is > 0.
   * Additive, backward compatible.
   */
  pendingDelayRechargeCents: z.number().int().nonnegative(),
});

export type PaymentConfigResponse = z.infer<typeof PaymentConfigResponseSchema>;

// ── CreatePaymentIntentRequest (JQ4-37) ───────────────────────────────────────
// Body for POST /visitor/payment/intent. tipAmountCents is optional (defaults to 0).
// Server re-validates against tip config — client value is not trusted.

export const CreatePaymentIntentRequestSchema = z.object({
  tipAmountCents: z.number().int().min(0).max(MAX_CUSTOM_TIP_CENTS).optional(),
});

export type CreatePaymentIntentRequest = z.infer<typeof CreatePaymentIntentRequestSchema>;

// ── CreatePaymentIntentResponse ───────────────────────────────────────────────

export const CreatePaymentIntentResponseSchema = z.object({
  /** Stripe PI id — persisted on the ticket for idempotency and webhook resolution. */
  paymentIntentId: z.string().min(1),
  /** Client secret for stripe.confirmPayment (REQ-2.2). */
  clientSecret: z.string().min(1),
  /** Current PI status (e.g. 'requires_payment_method'). */
  status: z.string().min(1),
  /** Server-computed breakdown (REQ-1.4 / CAP-1). */
  amount: ChargeBreakdownSchema,
  /** ISO 4217 3-letter currency code. */
  currency: z.string().length(3),
});

export type CreatePaymentIntentResponse = z.infer<typeof CreatePaymentIntentResponseSchema>;

// ── PaymentStatusResponse ─────────────────────────────────────────────────────

export const PaymentStatusResponseSchema = z.object({
  /** Stripe PI id. */
  paymentIntentId: z.string().min(1),
  /**
   * Live Stripe PI status for network-drop reconciliation (S5 / CAP-6).
   * Possible values: 'succeeded' | 'processing' | 'requires_payment_method' | 'canceled'.
   */
  status: z.string().min(1),
  /**
   * DB payment status on the ticket.
   * Stripe webhook is the authority for 'paid' (REQ-9.2).
   */
  paymentStatus: z.enum(['unpaid', 'paid', 'failed']),
});

export type PaymentStatusResponse = z.infer<typeof PaymentStatusResponseSchema>;
